import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentRequestAPI, commissionAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { DollarSign, CreditCard, User } from 'lucide-react';
import { useDialog } from '../../components/Dialog';
import PageHeader from './components/PageHeader';
import './CreatePaymentRequestPage.css';

const CreatePaymentRequestPage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showError, showSuccess, DialogComponent } = useDialog();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentCommission, setCurrentCommission] = useState(null);
  const [formData, setFormData] = useState({
    requestedAmount: '',
    bankName: '',
    accountNumber: '',
    accountHolderName: '',
  });

  useEffect(() => {
    fetchCommissionData();
  }, []);

  const fetchCommissionData = async () => {
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      const [commissionRes, requestsRes] = await Promise.all([
        commissionAPI.getDetails(currentMonth, currentYear),
        paymentRequestAPI.getAll(),
      ]);

      const commissionData = commissionRes.data.data || commissionRes.data;
      const requestsData = requestsRes.data?.data || requestsRes.data || [];

      // Calculate available balance by subtracting paid and pending requests for current month
      const totalPaid = requestsData
        .filter(req => req.month === currentMonth && req.year === currentYear && req.status === 'paid')
        .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);

      const totalPending = requestsData
        .filter(req => req.month === currentMonth && req.year === currentYear && ['pending', 'approved'].includes(req.status))
        .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);

      // Available balance = Net total - Paid - Pending
      const availableBalance = (commissionData?.netTotal || 0) - totalPaid - totalPending;

      setCurrentCommission({
        ...commissionData,
        totalPaid,
        totalPending,
        availableBalance,
      });
    } catch (error) {
      console.error('Failed to fetch commission:', error);
      showError(t('cannotLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const { requestedAmount, bankName, accountNumber, accountHolderName } = formData;

    if (!requestedAmount || parseFloat(requestedAmount) <= 0) {
      showError(t('pleaseEnterAmount'));
      return false;
    }

    if (parseFloat(requestedAmount) > (currentCommission?.availableBalance || 0)) {
      showError(t('amountExceedsAvailable'));
      return false;
    }

    if (!bankName || bankName.trim() === '') {
      showError(t('pleaseEnterBankName'));
      return false;
    }

    if (!accountNumber || accountNumber.trim() === '') {
      showError(t('pleaseEnterAccountNumber'));
      return false;
    }

    if (!accountHolderName || accountHolderName.trim() === '') {
      showError(t('pleaseEnterAccountHolderName'));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await paymentRequestAPI.create({
        requestedAmount: parseFloat(formData.requestedAmount),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        bankName: formData.bankName,
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName,
      });

      showSuccess(t('paymentRequestCreated'));
      setTimeout(() => {
        navigate('/loan/payment-request');
      }, 1500);
    } catch (error) {
      console.error('Failed to create payment request:', error);
      showError(error.response?.data?.msg || t('cannotCreateRequest'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  const availableBalance = currentCommission?.availableBalance || 0;

  return (
    <div className="create-payment-request-page">
      <DialogComponent />
      <PageHeader title={t('createPaymentRequest')} backTo="/loan/payment-request" />

      <div className="page-content">
        {/* Available Balance Banner */}
        <div className="balance-banner">
          <div className="balance-icon">
            <DollarSign size={24} />
          </div>
          <div className="balance-info">
            <div className="balance-label">{t('availableBalance')}</div>
            <div className="balance-value">{formatCurrency(availableBalance)}</div>
          </div>
          <div className="balance-date">
            {t('monthYear', {
              month: new Date().getMonth() + 1,
              year: new Date().getFullYear()
            })}
          </div>
        </div>

        {/* Form Section */}
        <div className="form-section">
          {/* Requested Amount */}
          <div className="form-group">
            <label className="form-label">
              <DollarSign size={18} />
              {t('requestedAmount')} *
            </label>
            <input
              type="number"
              className="form-input"
              placeholder={t('enterAmount')}
              value={formData.requestedAmount}
              onChange={(e) => handleInputChange('requestedAmount', e.target.value)}
              max={availableBalance}
            />
            <div className="form-helper">
              {t('maxAmount')}: {formatCurrency(availableBalance)}
            </div>
          </div>

          {/* Bank Name */}
          <div className="form-group">
            <label className="form-label">
              <CreditCard size={18} />
              {t('bankName')} *
            </label>
            <input
              type="text"
              className="form-input"
              placeholder={t('enterBankName')}
              value={formData.bankName}
              onChange={(e) => handleInputChange('bankName', e.target.value)}
            />
            <div className="form-helper">{t('exampleBank')}: Vietcombank, BIDV, ACB...</div>
          </div>

          {/* Account Number */}
          <div className="form-group">
            <label className="form-label">
              <CreditCard size={18} />
              {t('accountNumber')} *
            </label>
            <input
              type="text"
              className="form-input"
              placeholder={t('enterAccountNumber')}
              value={formData.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
            />
          </div>

          {/* Account Holder Name */}
          <div className="form-group">
            <label className="form-label">
              <User size={18} />
              {t('accountHolderName')} *
            </label>
            <input
              type="text"
              className="form-input"
              placeholder={t('enterAccountHolderName')}
              value={formData.accountHolderName}
              onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
            />
          </div>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <div className="info-icon">ℹ️</div>
          <div className="info-content">
            <p><strong>{t('note')}:</strong></p>
            <ul>
              <li>{t('paymentRequestNote1')}</li>
              <li>{t('paymentRequestNote2')}</li>
              <li>{t('paymentRequestNote3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Actions */}
      <div className="bottom-actions">
        <button
          className="btn-cancel"
          onClick={() => navigate('/loan/payment-request')}
          disabled={submitting}
        >
          {t('cancel')}
        </button>
        <button
          className="btn-submit"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? t('submitting') : t('submit')}
        </button>
      </div>
    </div>
  );
};

export default CreatePaymentRequestPage;
